/**
 * Live registry of onboarding hint target elements, keyed by hint id.
 *
 * A hint target is the real interactive DOM element a beacon points at (see
 * `useHintTarget` and `HintBeaconLayer`). The registry only ever holds elements
 * that are currently mounted - a component registers on mount and unregisters
 * on unmount. In-memory only, module-singleton, mirrors the subscribe/notify
 * shape of `hint-store.ts`.
 */

/** A registered hint host: the id from `HINTS` plus the element currently rendering it. */
export interface HintTarget {
  id: string;
  element: HTMLElement;
}

const targets = new Map<string, HTMLElement>();
const listeners = new Set<() => void>();

/**
 * Cached snapshot of `targets`, rebuilt only when a registration actually changes - keeps the
 * array reference stable across calls that see no change, which `useSyncExternalStore` needs to
 * avoid re-rendering on every unrelated store notification.
 */
let cachedTargetList: readonly HintTarget[] | null = null;

function invalidateTargetListCache(): void {
  cachedTargetList = null;
}

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

/** Registers the element for a hint id. A second call for the same id replaces the element - a remount must not leave a stale node. */
export function registerHintTarget(id: string, element: HTMLElement): void {
  targets.set(id, element);
  invalidateTargetListCache();
  notifyListeners();
}

/**
 * Drops the registered element for a hint id, but only if it still matches the
 * given element - guards against an out-of-order unmount clearing a newer
 * registration for the same id.
 */
export function unregisterHintTarget(id: string, element: HTMLElement): void {
  if (targets.get(id) !== element) return;
  targets.delete(id);
  invalidateTargetListCache();
  notifyListeners();
}

/** The live list of registered targets. Stable array identity between calls that see no change. */
export function getHintTargets(): readonly HintTarget[] {
  if (cachedTargetList === null) {
    cachedTargetList = Array.from(targets, ([id, element]) => ({
      id,
      element,
    }));
  }
  return cachedTargetList;
}

/** Subscribes to every registration change (register, unregister). Returns an unsubscribe function. */
export function subscribeHintTargets(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Re-runs beacon layout without changing the registry — use when an opaque overlay appears
 * over already-registered hosts (directory tip card) so covered beacons drop immediately.
 * Invalidates the cached snapshot so useSyncExternalStore sees a new array reference and
 * schedules a re-measure.
 */
export function notifyHintTargetsChanged(): void {
  invalidateTargetListCache();
  notifyListeners();
}

/** Test-only helper - drops every registered target and subscriber. */
export function _resetHintTargetsForTests(): void {
  targets.clear();
  listeners.clear();
  invalidateTargetListCache();
}
